// Core API utilities: URL resolution, request helpers, data transforms.
// Every domain service depends on these.
import { coalescer } from "../utils/requestCoalescer";
import { isNative, getNativeToken, clearNativeToken } from "../../lib/nativeBridge";

export const getAPIBaseURL = () => {
  if (typeof window === "undefined") {
    console.warn(
      "[API] getAPIBaseURL called outside browser context - returning safe placeholder",
    );
    return "http://localhost:8000";
  }

  if (process.env.NODE_ENV === "development") {
    const explicitBackendUrl = process.env.REACT_APP_BACKEND_URL;

    // Explicit override wins, unless it points at the local edge API and
    // REACT_APP_USE_LOCAL_EDGE_API is not set to "1" (opt-in).
    if (
      explicitBackendUrl &&
      !(
        explicitBackendUrl.includes("localhost:8787") &&
        process.env.REACT_APP_USE_LOCAL_EDGE_API !== "1"
      )
    ) {
      return explicitBackendUrl;
    }
    // Default dev traffic to same-origin /api: the vite dev server proxies
    // it to the local worker (http://localhost:8787). Talking to production
    // from a local dev shell meant reading and writing real user data.
    return "/api";
  }

  const host = window.location.hostname || "";
  const isCloudflarePagesHost =
    host === "www.stagster.com" ||
    host === "stagster.com" ||
    host.endsWith(".stagster.pages.dev") ||
    host.endsWith(".pages.dev");

  // In production, auth must use the same origin so the HttpOnly
  // stagster_session cookie set by /api/auth/google/callback is sent back
  // to /api/auth/session. A baked REACT_APP_BACKEND_URL pointing at
  // workers.dev breaks login because browser cookies are host-scoped.
  if (isCloudflarePagesHost) {
    return "/api";
  }

  if (process.env.REACT_APP_BACKEND_URL) {
    return process.env.REACT_APP_BACKEND_URL;
  }
  return "/api";
};

export const API_BASE_URL = getAPIBaseURL();

export const getAPIRoot = () => {
  const base = getAPIBaseURL();
  return base === "/api" ? "/api" : `${base}/api`;
};

export const buildIdentityHeaders = ({ token = null, headers = {} } = {}) => ({
  ...(token ? { Authorization: `Bearer ${token}` } : {}),
  ...headers,
});

// Standard /api-prefixed call with deduping, 403-suppression, dev API block.
export const apiCall = async (endpoint, options = {}) => {
  if (typeof window === "undefined" || typeof fetch === "undefined") {
    console.warn(
      "[API] apiCall attempted outside browser context - blocking request",
    );
    throw new Error("API calls are only available in browser environment");
  }

  const fullUrl = `${getAPIRoot()}${endpoint}`;

  if (
    process.env.NODE_ENV === "development" &&
    fullUrl.includes("api.stagster.com")
  ) {
    console.error(
      "[API] BLOCKED: Attempted to call production API during development:",
      fullUrl,
    );
    throw new Error("Production API calls are blocked in development mode");
  }

  try {
    const shouldLog =
      process.env.NODE_ENV !== "production" &&
      !fullUrl.includes("/fastlane/request/status");
    if (shouldLog) {
      console.log(`🌐 API Call: ${fullUrl}`);
    }

    const method = (options.method || "GET").toString().toUpperCase();
    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };
    if (options.body != null && method !== "GET") {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
    }

    // Native shell (Sprint 1): attach the stored session JWT as a bearer token.
    // On the web this branch is inert (isNative() is false) so the cookie flow
    // is byte-identical.
    let nativeToken = null;
    if (isNative() && !headers.Authorization) {
      nativeToken = await getNativeToken();
      if (nativeToken) headers.Authorization = `Bearer ${nativeToken}`;
    }

    const fetchOpts = {
      credentials: options.credentials || "include",
      headers,
      ...options,
    };
    fetchOpts.headers = headers;
    const doFetch = async () => fetch(fullUrl, fetchOpts);
    const rawResponse =
      method === "GET"
        ? await coalescer.run(`GET ${fullUrl}`, doFetch)
        : await doFetch();

    const response =
      method === "GET" && rawResponse?.clone
        ? rawResponse.clone()
        : rawResponse;

    if (!response.ok) {
      const errorText = await response.text();
      const error = new Error(
        `API Error: ${response.status} ${response.statusText} - ${errorText}`,
      );
      error.status = response.status;

      if (response.status === 403) {
        return null;
      }

      // Native: a 401 with a stored token means the session expired — drop it
      // so the next sign-in starts clean.
      if (response.status === 401 && nativeToken) {
        await clearNativeToken();
      }

      throw error;
    }

    const data = await response.json();
    return data;
  } catch (error) {
    if (error.status === 403 || error.message?.includes("403")) {
      return null;
    }

    console.error("❌ API call failed:", {
      endpoint,
      fullUrl,
      error: error.message,
      stack: error.stack,
    });
    throw error;
  }
};

// Non-/api endpoint call (e.g. venue portal) with optional credentials.
export const apiCallAbsolute = async (endpoint, options = {}) => {
  const baseUrl = getAPIBaseURL();
  const fullUrl = `${baseUrl}${endpoint}`;

  try {
    if (process.env.NODE_ENV !== "production") {
      console.log(`🌐 API Call: ${fullUrl}`);
    }

    const method = (options.method || "GET").toString().toUpperCase();
    const headers = {
      Accept: "application/json",
      ...(options.headers || {}),
    };
    if (options.body != null && method !== "GET") {
      headers["Content-Type"] = headers["Content-Type"] || "application/json";
    }

    const fetchOpts = {
      headers,
      credentials: options.credentials || "include",
      ...options,
    };
    fetchOpts.headers = headers;

    const doFetch = async () => fetch(fullUrl, fetchOpts);
    const rawResponse =
      method === "GET"
        ? await coalescer.run(`ABSGET ${fullUrl}`, doFetch)
        : await doFetch();
    const response =
      method === "GET" && rawResponse?.clone
        ? rawResponse.clone()
        : rawResponse;

    const text = await response.text();
    const payload = text
      ? (() => {
          try {
            return JSON.parse(text);
          } catch {
            return text;
          }
        })()
      : null;

    if (!response.ok) {
      const error = new Error(
        (payload && (payload.message || payload.detail)) ||
          `API Error: ${response.status} ${response.statusText}`,
      );
      error.status = response.status;
      error.payload = payload;
      throw error;
    }

    return payload;
  } catch (error) {
    const suppress =
      options &&
      (options.suppressErrorLog || (options.meta && options.meta.silent));
    if (!suppress) {
      console.error("❌ Absolute API call failed:", {
        endpoint,
        fullUrl,
        error: error.message,
      });
    }
    throw error;
  }
};

export const withErrorHandling = async (apiCall, fallbackData = null) => {
  try {
    return await apiCall();
  } catch (error) {
    console.error("API call failed, using fallback:", error);
    return fallbackData;
  }
};

// Normalize raw venue data from API to the shape the UI expects.
export const transformVenueData = (venue) => {
  const sourceVibe =
    (venue.venue_type || venue.vibe || venue.type || "cafe")
      ?.toString()
      .toLowerCase() || "cafe";
  const priceRangeRaw =
    (venue.price_range || venue.priceRange || "mid")
      ?.toString()
      .toLowerCase() || "mid";
  const getFallbackImage = (vibe) => {
    const fallbackImages = {
      cafe: "/venues/Chai_Point_indiranagar.webp",
      bar: "/venues/social_blr.webp",
      pub: "/venues/arbor-brewing.webp",
      restaurant: "/venues/Skyye_blr.jpg",
      brewpub: "/venues/toit-brewpub.jpg",
    };
    return fallbackImages[vibe] || "/venues/work-from-bar.webp";
  };

  let imageUrl =
    venue.image_url ||
    venue.photo_url ||
    venue.image ||
    getFallbackImage(sourceVibe);

  if (imageUrl) {
    const lowerUrl = imageUrl.toLowerCase();

    const isProfileImage =
      lowerUrl.includes("profile") ||
      lowerUrl.includes("avatar") ||
      lowerUrl.includes("user") ||
      lowerUrl.includes("person") ||
      lowerUrl.includes("face") ||
      lowerUrl.includes("portrait") ||
      lowerUrl.includes("headshot") ||
      lowerUrl.includes("selfie") ||
      lowerUrl.includes("pfp") ||
      lowerUrl.includes("sai") ||
      lowerUrl.includes("sarah") ||
      lowerUrl.includes("women") ||
      /\bmen\b/.test(lowerUrl) ||
      lowerUrl.includes("girl") ||
      lowerUrl.includes("boy") ||
      (lowerUrl.includes("photo") && !lowerUrl.includes("venue")) ||
      (lowerUrl.includes("image") && !lowerUrl.includes("venue"));

    const isDemoImage =
      lowerUrl.includes("demo") ||
      lowerUrl.includes("test") ||
      lowerUrl.includes("placeholder") ||
      lowerUrl.includes("default") ||
      lowerUrl.includes("temp") ||
      lowerUrl.includes("sample") ||
      lowerUrl.includes("example") ||
      lowerUrl.includes("mock") ||
      lowerUrl.includes("fake") ||
      lowerUrl.includes("dummy");

    if (isProfileImage || isDemoImage) {
      const correctedImageUrl = getFallbackImage(sourceVibe);
      imageUrl = correctedImageUrl;
    }
  }

  try {
    const lower = (imageUrl || "").toString().toLowerCase();
    const isHttp = lower.startsWith("http://") || lower.startsWith("https://");
    if (isHttp) {
      const u = new URL(imageUrl);
      const allowedHosts = new Set([
        "images.unsplash.com",
        "images.pexels.com",
        "lh3.googleusercontent.com",
        "maps.googleapis.com",
        "b.zmtcdn.com",
        "im1.dineout.co.in",
        "localhost",
        "localhost:8000",
        "localhost:8001",
        "stagster.com",
        "www.stagster.com",
        "images.stagster.com",
      ]);
      const hostAllowed = allowedHosts.has(u.host);
      const pathLooksVenue = u.pathname.toLowerCase().includes("/venues");
      if (!hostAllowed && !pathLooksVenue) {
        if (process.env.NODE_ENV !== "production") {
          console.warn(
            "🔒 Blocking non-venue image host for venue:",
            venue.name,
            "host:",
            u.host,
          );
        }
        imageUrl = getFallbackImage(sourceVibe);
      }
    }
  } catch (_) {
    imageUrl = getFallbackImage(sourceVibe);
  }

  const getVenueSpecificImage = (venueName, defaultImage) => {
    if (!venueName) return defaultImage;
    const venueNameLower = venueName.toLowerCase().trim();

    const exactMatches = {
      "toit brewpub": "/venues/toit-brewpub.jpg",
      "toit brewpub indiranagar": "/venues/toit-brewpub.jpg",
      "toit indiranagar": "/venues/toit-brewpub.jpg",
      "ub city bar": "/venues/UBCity.jpg",
      "ub city": "/venues/UBCity.jpg",
      "arbor brewing company": "/venues/arbor-brewing.webp",
      "arbor brewing": "/venues/arbor-brewing.webp",
      "chai point indiranagar": "/venues/Chai_Point_indiranagar.webp",
      "chai point": "/venues/Chai_Point_indiranagar.webp",
      "social koramangala": "/venues/social.jpg",
      "social house": "/venues/social.jpg",
      "aaromalé cafe & creative community":
        "/venues/aaromale-cafe-hyderabad.jpg",
      "aaromale cafe & creative community":
        "/venues/aaromale-cafe-hyderabad.jpg",
    };

    if (exactMatches[venueNameLower]) {
      return exactMatches[venueNameLower];
    }

    const partialMatches = [
      { keywords: ["toit", "brewpub"], image: "/venues/toit-brewpub.jpg" },
      { keywords: ["ub", "city", "bar"], image: "/venues/UBCity.jpg" },
      { keywords: ["arbor", "brewing"], image: "/venues/arbor-brewing.webp" },
      {
        keywords: ["chai", "point"],
        image: "/venues/Chai_Point_indiranagar.webp",
      },
      {
        keywords: ["social", "koramangala"],
        image:
          "https://images.unsplash.com/photo-1514933651103-005eec06c04b?w=500&h=400&fit=crop&auto=format&q=80",
      },
      {
        keywords: ["aaromale", "creative"],
        image: "/venues/aaromale-cafe-hyderabad.jpg",
      },
    ];

    for (const match of partialMatches) {
      if (match.keywords.every((keyword) => venueNameLower.includes(keyword))) {
        return match.image;
      }
    }

    return defaultImage;
  };

  const finalImageUrl = getVenueSpecificImage(venue.name, imageUrl);

  const vibeToType = {
    cafe: "cafe",
    bar: "bar",
    pub: "pub",
    restaurant: "restaurant",
    brewpub: "brewpub",
  };

  const generatedTags = [];
  if (sourceVibe === "cafe") generatedTags.push("☕ Coffee & Work");
  if (sourceVibe === "bar") generatedTags.push("🍸 Bar & Drinks");
  if (sourceVibe === "pub") generatedTags.push("🍺 Pub & Social");
  if (sourceVibe === "brewpub") generatedTags.push("🍺 Brewpub & Craft Beer");
  if (sourceVibe === "restaurant") generatedTags.push("🍽️ Restaurant & Food");
  const tags =
    Array.isArray(venue.tags) && venue.tags.length > 0
      ? venue.tags
      : generatedTags;

  const priceMapping = {
    budget: "₹ Budget",
    mid: "₹₹ Mid-range",
    "mid-range": "₹₹ Mid-range",
    medium: "₹₹ Mid-range",
    moderate: "₹₹ Mid-range",
    premium: "₹₹₹ Premium",
  };

  const normalizeLiveStatus = (raw) => {
    if (!raw || typeof raw !== "object") {
      return null;
    }
    const status = raw.status || raw.tone || "neutral";
    const label = raw.label || raw.status_label || raw.message || "";
    return {
      status,
      label,
      sampleCount: raw.sampleCount ?? raw.sample_count ?? null,
      breakdown: raw.breakdown || raw.counts || {},
      lastReport: raw.lastReport || raw.last_report || null,
      recentOutcome: raw.recentOutcome || raw.recent_outcome || null,
      recentArrivalWindow:
        raw.recentArrivalWindow || raw.recent_arrival_window || null,
      recentNotes: raw.recentNotes || raw.recent_notes || "",
      confidence: raw.confidence ?? raw.confidence_score ?? null,
      thresholdMet: raw.thresholdMet ?? raw.threshold_met ?? false,
    };
  };

  const liveStatus = normalizeLiveStatus(venue.live_status || venue.liveStatus);

  const wifi =
    venue.wifi ??
    (venue.has_wifi === true
      ? "available"
      : venue.has_wifi === false
        ? "none"
        : null);
  const power =
    venue.power ??
    (venue.has_power === true
      ? "available"
      : venue.has_power === false
        ? "none"
        : null);
  const quiet = venue.quiet ?? null;

  const primaryWorkFriendly =
    typeof venue.is_work_friendly === "boolean"
      ? venue.is_work_friendly
      : typeof venue.isWorkFriendly === "boolean"
        ? venue.isWorkFriendly
        : undefined;
  const derivedWorkFriendly =
    typeof venue.workFriendly === "boolean" ? venue.workFriendly : undefined;
  const workFriendly =
    primaryWorkFriendly !== undefined
      ? primaryWorkFriendly
      : derivedWorkFriendly;
  const stagFriendly =
    typeof venue.stagFriendly === "boolean"
      ? venue.stagFriendly
      : typeof venue.is_stag_friendly === "boolean"
        ? venue.is_stag_friendly
        : undefined;
  const womenSafe =
    typeof venue.womenSafe === "boolean"
      ? venue.womenSafe
      : (venue.is_women_safe ?? undefined);

  const normalizeWarning = (warning) => {
    if (!warning || typeof warning !== "object") return null;
    const start = warning.start_at || warning.startAt;
    const end = warning.end_at || warning.endAt;
    const severity = (warning.severity || "warning").toLowerCase();
    return {
      id: warning.id,
      venueId: warning.venue_id || warning.venueId || venue.id,
      reason: (warning.reason || "").trim(),
      severity: ["info", "warning", "critical"].includes(severity)
        ? severity
        : "warning",
      startAt: start,
      endAt: end,
      metadata:
        warning.metadata && typeof warning.metadata === "object"
          ? warning.metadata
          : {},
      createdBy: warning.created_by || warning.createdBy || null,
      createdAt: warning.created_at || warning.createdAt || null,
      updatedAt: warning.updated_at || warning.updatedAt || null,
    };
  };

  const rawWarnings = Array.isArray(venue.event_warnings)
    ? venue.event_warnings
    : Array.isArray(venue.eventWarnings)
      ? venue.eventWarnings
      : [];
  const normalizedWarnings = rawWarnings.map(normalizeWarning).filter(Boolean);

  let primaryWarning = null;
  if (venue.event_warning || venue.eventWarning) {
    primaryWarning = normalizeWarning(
      venue.event_warning || venue.eventWarning,
    );
  } else if (normalizedWarnings.length > 0) {
    primaryWarning = normalizedWarnings[0];
  }
  if (
    primaryWarning &&
    normalizedWarnings.every((item) => item?.id !== primaryWarning.id)
  ) {
    normalizedWarnings.unshift(primaryWarning);
  }

  return {
    id: venue.id,
    name: venue.name,
    image: finalImageUrl,
    tags,
    distance: typeof venue.distance === "number" ? venue.distance : null,
    type: venue.venue_type || venue.type || vibeToType[sourceVibe] || null,
    rating: typeof venue.rating === "number" ? venue.rating : null,
    workFriendly,
    stagFriendly,
    womenSafe,
    description: venue.description || "",
    priceRangeCode: priceRangeRaw,
    priceRange: priceMapping[priceRangeRaw] || priceRangeRaw,
    crowdType: venue.crowdType || null,
    parking: venue.parking || null,
    min_order: venue.min_order || venue.minOrder || "",
    wifi,
    power,
    quiet,
    address: venue.address,
    city: venue.city,
    vibe: sourceVibe,
    best_time_peak: venue.best_time_peak || null,
    best_time_quiet: venue.best_time_quiet || null,
    seating: Array.isArray(venue.seating) ? venue.seating : [],
    stag_solo_entry: venue.stag_solo_entry || null,
    is_featured: venue.is_featured || false,
    is_spotlight: venue.is_spotlight || false,
    is_stagster_certified: venue.is_stagster_certified || false,
    is_budget: venue.is_budget || false,
    is_stag_friendly: venue.is_stag_friendly || false,
    has_user_thumbs_up: venue.has_user_thumbs_up || false,
    is_work_certified: venue.is_work_certified || false,
    has_quiet_zones: venue.has_quiet_zones || false,
    is_sponsored: venue.is_sponsored || false,
    is_work_friendly: workFriendly,
    image_url: finalImageUrl,
    gallery_images: Array.isArray(venue.gallery_images) ? venue.gallery_images : [],
    galleryImages: Array.isArray(venue.gallery_images) ? venue.gallery_images : [],
    editorial_tip: venue.editorial_tip || "",
    smoking_zone: venue.smoking_zone || null,
    music_volume: venue.music_volume || null,
    crowd_status: venue.crowd_status || null,
    crowd_description: venue.crowd_description || null,
    openNow: venue.open_now || false,
    listingChannel: venue.listing_channel || "stag",
    eventWarning: primaryWarning,
    eventWarnings: normalizedWarnings,
    menu_images: Array.isArray(venue.menu_images) ? venue.menu_images : [],
    menu_source_url: venue.menu_source_url || null,
    menu_updated_at: venue.menu_updated_at || null,
    has_menu: Array.isArray(venue.menu_images) && venue.menu_images.length > 0,
    liveStatus,
  };
};

export const clearAllCache = async () => {
  const exactKeys = ["fs_auth_cache_v1"];
  const prefixes = ["fs_unified_cache_v1"];

  if (typeof window !== "undefined") {
    const stores = [window.sessionStorage, window.localStorage];
    stores.forEach((store) => {
      if (!store) return;
      try {
        exactKeys.forEach((key) => store.removeItem(key));
        prefixes.forEach((prefix) => {
          const matches = [];
          for (let i = 0; i < store.length; i += 1) {
            const key = store.key(i);
            if (key && key.startsWith(`${prefix}:`)) {
              matches.push(key);
            }
          }
          matches.forEach((key) => store.removeItem(key));
        });
      } catch (_) {
        // Storage may be disabled; ignore
      }
    });

    if (window.caches?.keys) {
      try {
        const cacheNames = await window.caches.keys();
        await Promise.all(cacheNames.map((name) => window.caches.delete(name)));
      } catch (_) {
        // Ignore cache deletion failures
      }
    }
  }
};
